import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { Icon } from './Icons';
import { btnGhost, btnIcon, tableStyle, tdStyle, thStyle } from './buttons';

export interface Column<Row> {
  key: string;
  header: string;
  /** What to render in the cell. Defaults to `row[key]` if not provided. */
  render?: (row: Row) => ReactNode;
  /** Sort accessor. If omitted, the column isn't sortable. */
  sortKey?: keyof Row | ((row: Row) => string | number);
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  /** Hide cell on narrow screens (uses .hide-mobile from theme.css). */
  hideMobile?: boolean;
}

interface Props<Row> {
  rows: Row[];
  columns: Column<Row>[];
  rowKey: (row: Row) => string | number;
  emptyState?: ReactNode;
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  toolbar?: ReactNode;
  pageSize?: number;
  onRowClick?: (row: Row) => void;
  loading?: boolean;
}

/**
 * Generic data table — sort + paginate client-side. Server-side filter (if
 * any) is up to the caller via `rows` + `search.value`. Keeps the UI as
 * snappy as the prototype: no virtualization for now since N is usually < 1k.
 */
export function DataTable<Row>({
  rows,
  columns,
  rowKey,
  emptyState = 'No rows.',
  search,
  toolbar,
  pageSize = 50,
  onRowClick,
  loading,
}: Props<Row>) {
  const [sortBy, setSortBy] = useState<{ index: number; dir: 'asc' | 'desc' } | null>(null);
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    if (!sortBy) return rows;
    const col = columns[sortBy.index];
    if (!col?.sortKey) return rows;
    const dir = sortBy.dir === 'asc' ? 1 : -1;
    const accessor = typeof col.sortKey === 'function'
      ? col.sortKey
      : (r: Row) => (r as any)[col.sortKey as string];
    return [...rows].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
  }, [rows, columns, sortBy]);

  const total = sorted.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pages);
  const slice = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (index: number) => {
    const col = columns[index];
    if (!col.sortKey) return;
    setSortBy((prev) => {
      if (!prev || prev.index !== index) return { index, dir: 'desc' };
      return prev.dir === 'desc' ? { index, dir: 'asc' } : null;
    });
    setPage(1);
  };

  return (
    <div>
      {(search || toolbar) && (
        <div
          style={{
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            borderBottom: '1px solid var(--line)',
          }}
        >
          {search && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'var(--bg-sunken)',
                border: '1px solid var(--line)',
                padding: '6px 10px',
                borderRadius: 'var(--r-md)',
                color: 'var(--ink-3)',
                flex: '1 1 220px',
                minWidth: 200,
                maxWidth: 340,
              }}
            >
              <Icon.Search />
              <input
                value={search.value}
                onChange={(e) => {
                  search.onChange(e.target.value);
                  setPage(1);
                }}
                placeholder={search.placeholder ?? 'Search…'}
                style={{
                  border: 0,
                  background: 'transparent',
                  outline: 'none',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  color: 'var(--ink)',
                  flex: 1,
                }}
              />
            </div>
          )}
          {toolbar && <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>{toolbar}</div>}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {columns.map((c, i) => {
                const active = sortBy?.index === i;
                const sortable = !!c.sortKey;
                return (
                  <th
                    key={c.key}
                    onClick={sortable ? () => handleSort(i) : undefined}
                    className={c.hideMobile ? 'hide-mobile' : undefined}
                    style={{
                      ...thStyle,
                      textAlign: c.align ?? 'left',
                      width: c.width,
                      cursor: sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        color: active ? 'var(--ink)' : 'var(--ink-3)',
                      }}
                    >
                      {c.header}
                      {sortable && (
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, opacity: active ? 1 : 0.3 }}>
                          {active ? (sortBy!.dir === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {slice.map((row) => {
              const key = rowKey(row);
              const clickable = !!onRowClick;
              return (
                <tr
                  key={key}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                  style={{
                    borderTop: '1px solid var(--line)',
                    cursor: clickable ? 'pointer' : 'default',
                    transition: 'background 0.12s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (clickable) e.currentTarget.style.background = 'var(--bg-sunken)';
                  }}
                  onMouseLeave={(e) => {
                    if (clickable) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={c.hideMobile ? 'hide-mobile' : undefined}
                      style={{ ...tdStyle, textAlign: c.align ?? 'left' }}
                    >
                      {c.render ? c.render(row) : ((row as any)[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              );
            })}
            {slice.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ ...tdStyle, textAlign: 'center', color: 'var(--ink-3)', padding: '40px 0' }}
                >
                  {loading ? 'Loading…' : emptyState}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div
          style={{
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--line)',
            fontSize: 12,
            color: 'var(--ink-3)',
          }}
        >
          <span>
            Showing <span className="mono" style={{ color: 'var(--ink)' }}>{slice.length}</span> of <span className="mono">{total}</span>
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              style={{ ...btnIcon, width: 30, height: 30 }}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              ‹
            </button>
            <span style={{ display: 'inline-grid', placeItems: 'center', minWidth: 60, fontSize: 12, color: 'var(--ink-3)' }}>
              page {currentPage} / {pages}
            </span>
            <button
              style={{ ...btnIcon, width: 30, height: 30 }}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={currentPage >= pages}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const StaleButton = btnGhost;
